import { type Ref, useEffect, useState } from 'react';

import { fetchEndpointData } from 'api';
import Button from 'components/Button';
import { ComboboxOption, ComboboxOptionGroup } from 'components/Combobox';
import HighlightedText from 'components/HighlightedText';
import Icon, { type IconName } from 'components/Icon';
import Link from 'components/Link';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import ProductLine from 'components/ProductLine';
import {
	SearchComboboxContextProvider,
	SearchComboboxWithoutContext,
	useSearchComboboxContext,
} from 'components/SearchCombobox';
import Text from 'components/Text';
import { SEARCH_QUERY_PARAM } from 'constants/queryParams';
import {
	useGlobalLinks,
	useMaxWidth,
	useSearch,
	useSearchProductsPageSize,
	useSearchTermsPageSize,
	useValueChangeEffect,
	useVirtualKeyboardSize,
} from 'hooks';
import { usePopularProducts, usePopularSearchTerms } from 'hooks/data-fetching';
import { useProductListGTMEvents, useSearchTermGTM } from 'hooks/gtm';
import { useNavigation, useSearchParams } from 'hooks/navigation';
import type { Store } from 'models/api';
import type { ProductCard } from 'models/productCard';
import type { InspirationPage } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { getTestDataAttrFrom, is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { matchesProductId } from 'utils/string';
import { createUrl } from 'utils/url';

function getSearchPageUrl(
	searchPagePath: string | undefined,
	searchQuery: string,
	extraParams: Record<string, string> = {},
) {
	// TODO: replace with assert object assert after merging company site improvements.
	if (!is.string(searchPagePath)) {
		throw new Error('getSearchPageUrl searchPagePath missing');
	}
	return createUrl(
		searchPagePath,
		{ [SEARCH_QUERY_PARAM]: searchQuery, ...extraParams },
		// Keep query first
		{ sortParams: false },
	);
}

interface InnerProps {
	/** Container class name */
	className?: string;

	/** Field test ID */
	dataCy: string;

	popularProductsText: string;

	popularSearchTermsText: string;
	ref?: Ref<HTMLDivElement>;
	searchBrandSuggestionLabel: string;
	searchCancelButtonText: string;
	searchCategorySuggestionLabel: string;
	searchFieldLabel: string;
	searchFieldPlaceholderText: string;
	searchInspirationSuggestionLabel: string;
	searchNoResultsText: string;
	searchOtherSearchTermRecommendationsText: string;
	searchResultProductsText: string;
	searchResultsSpellingSuggestionText: string;
	searchResultsText: string;
	searchStoreSuggestionLabel: string;
	searchSuggestionLabel: string;
}

interface Props extends InnerProps {
	/** Field id */
	id?: string;

	/** Combobox close callback */
	onClose?: () => void;
}

interface TermItem {
	icon?: IconName;
	key: string;
	screenReaderPrefix?: string;
	sendGTMEvent?: () => void;
	suffix?: string;
	text: string;
	url: string;
}

/** Search field with results dropdown. */
function PageHeaderSearchCombobox({
	className,
	dataCy,
	popularProductsText,
	popularSearchTermsText,
	ref,
	searchBrandSuggestionLabel,
	searchCancelButtonText,
	searchCategorySuggestionLabel,
	searchFieldLabel,
	searchFieldPlaceholderText,
	searchInspirationSuggestionLabel,
	searchNoResultsText,
	searchOtherSearchTermRecommendationsText,
	searchResultProductsText,
	searchResultsSpellingSuggestionText,
	searchResultsText,
	searchStoreSuggestionLabel,
	searchSuggestionLabel,
}: InnerProps) {
	const { navigate } = useNavigation();
	const { t } = useI18n();
	const { search } = useGlobalLinks();
	const isMaxMd = useMaxWidth('md');
	const keyboardSize = useVirtualKeyboardSize();

	const { sendSearchTermBrandSelectEvent, sendSearchTermCategorySelectEvent } =
		useSearchTermGTM();

	const {
		baseId,
		blurInput,
		closeCombobox,
		focusAfterSearch,
		hasOpened,
		hasSearchQuery,
		isOpen,
		listboxProps,
		rawInputValue,
		searchQuery,
		selectedOptionId,
		setInputValue,
		useFormSubmit,
	} = useSearchComboboxContext();

	const {
		brands: searchResultBrands,
		categories: searchResultCategories,
		hasResultResponse,
		inspirationPages: searchResultInspirationPages,
		isLoading: isLoadingResults,
		products: searchResultProducts,
		searchTerms: searchResultTerms,
		spellingSuggestions: searchResultSpellingSuggestions,
		stores: searchResultStores,
		totalProductCount,
		// Checking hasOpened instead of isOpen to have the results render even
		// if closed, when they have been loaded. Then they can be focused when
		// pressing down arrow from a closed state.
	} = useSearch(hasOpened ? searchQuery : '');

	// The search query that was used for the currently displayed result.
	// The query is used for things like matching word highlighting and "no
	// results for X" text and using `searchQuery` for that would change those
	// things in the current result before the new result matching the new query
	// is loaded, since `searchQuery` is just the debounced input value.
	const [resultQuery, setResultQuery] = useState('');
	useValueChangeEffect(isLoadingResults, () => {
		if (!isLoadingResults) {
			setResultQuery(searchQuery);
		}
	});

	const hasStores = searchResultStores.length > 0;
	const hasInspirationPages = searchResultInspirationPages.length > 0;
	const hasSearchTerms = searchResultTerms.length > 0;
	const hasCategories = searchResultCategories.length > 0;
	const hasBrands = searchResultBrands.length > 0;
	const hasProducts = searchResultProducts.length > 0;
	const hasSpellingSuggestions = searchResultSpellingSuggestions.length > 0;
	const hasResults =
		hasSearchQuery &&
		(hasSearchTerms ||
			hasCategories ||
			hasBrands ||
			hasProducts ||
			hasStores ||
			hasInspirationPages);

	// It looks like there is unnecessary duplication here (e.g. checking both
	// showSkeleton and hasResultResponse), but it's necessary to avoid some
	// flickering edge cases.
	const showSkeleton = hasSearchQuery && isLoadingResults && !hasResultResponse;
	const showNoResultsText =
		!showSkeleton && hasSearchQuery && !hasResults && hasResultResponse;
	const showResults = !showSkeleton && hasSearchQuery && hasResultResponse;
	const showPopular = (!showSkeleton && !showResults) || showNoResultsText;
	const showItems = showPopular || showResults;

	const isGroupTitleHidden =
		showSkeleton ||
		(!hasSpellingSuggestions && (showNoResultsText || hasResults));

	// Checking hasOpened for the same reason as for useSearch, see above.
	// The search query check is to avoid unnecessary fetching when the popular
	// items won't be displayed, since there is a search result to show instead.
	const productsPageSize = useSearchProductsPageSize();
	const termsPageSize = useSearchTermsPageSize();
	const { data: popularProducts, isLoading: isLoadingPopularProducts } =
		usePopularProducts({
			isActive: hasOpened,
			itemLimit: productsPageSize,
		});
	const { isLoading: isLoadingPopularTerms, terms: popularTerms } =
		usePopularSearchTerms({
			isActive: hasOpened,
			itemLimit: termsPageSize,
		});

	// Map search/spelling suggestions, categories and brands to a common format
	// for easier rendering.
	const termItems = [
		// stores
		...(showResults ? searchResultStores : []).map((store): TermItem => ({
			icon: 'store',
			key: `store-${store.id}`,
			text: store.name,
			url: store.url,
			screenReaderPrefix: searchStoreSuggestionLabel,
		})),
		// Spelling suggestions
		...(hasSpellingSuggestions ? searchResultSpellingSuggestions : []).map(
			(term): TermItem => ({
				icon: 'search',
				key: `spelling-${term}`,
				screenReaderPrefix: searchSuggestionLabel,
				text: term,
				url: getSearchPageUrl(search, term),
			}),
		),
		// Search suggestions
		...(hasSearchQuery && hasResults ? searchResultTerms : popularTerms).map(
			(term): TermItem => ({
				icon: 'search',
				key: `suggestion-${term}`,
				screenReaderPrefix: searchSuggestionLabel,
				text: term,
				url: getSearchPageUrl(search, term),
			}),
		),
		// Categories
		...(showResults ? searchResultCategories : []).map((cat): TermItem => ({
			key: `category-${cat.name}-${cat.parentName}`,
			text: cat.name,
			screenReaderPrefix: searchCategorySuggestionLabel,
			sendGTMEvent: () => sendSearchTermCategorySelectEvent(),
			suffix: cat.parentName ? `(${cat.parentName})` : '',
			url: cat.url,
		})),
		// Brands
		...(showResults ? searchResultBrands : []).map((brand): TermItem => ({
			key: `brand-${brand.name}`,
			text: brand.name,
			sendGTMEvent: () => sendSearchTermBrandSelectEvent(),
			suffix: `(${searchBrandSuggestionLabel})`,
			url: brand.url,
		})),
		// Inspiration pages
		...(showResults ? searchResultInspirationPages : []).map(
			(page): TermItem => ({
				key: `inspiration-${page.displayName}`,
				text: page.text,
				suffix: `(${searchInspirationSuggestionLabel})`,
				url: page.url,
			}),
		),
	];
	const productItems = hasResults ? searchResultProducts : popularProducts;

	const { sendSelectItemEvent, sendViewItemListEvent } =
		useProductListGTMEvents(
			'search_autocomplete',
			'Search result autocomplete',
		);
	const resultProductIds = searchResultProducts.map(({ id }) => id).join(',');
	useEffect(() => {
		if (searchResultProducts.length > 0) {
			sendViewItemListEvent(searchResultProducts, searchResultProducts.length);
		}
		// Only check IDs in case the results array changes but it still contains
		// the same products.
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [resultProductIds]);

	// Add overlay to page if search is active
	useEffect(() => {
		if (isOpen) {
			document.body.classList.add('search-overlay');
		}
		return () => {
			document.body.classList.remove('search-overlay');
		};
	}, [isOpen]);

	const searchParams = useSearchParams();
	// Sync the search field with changes to the URL
	const query = searchParams.get('query');
	useValueChangeEffect(query, () => {
		setInputValue(query || '');
	});

	// TODO: Maybe block subsequent submits if fetch is in progress?
	useFormSubmit(async () => {
		const inputValue = rawInputValue.trim();
		if (inputValue) {
			let singleItem: ProductCard | InspirationPage | Store | undefined;
			// Quickly (e.g. with a barcode scanner) entering a product ID and
			// pressing enter (to trigger submit) is likely only done by staff.
			// Run an extra request right away if it looks like this is the case,
			// ie the input is a product ID.
			// With the current setup it does not work trying use the useSearch result
			// since it might be stale leading to a mismatch between what the user put in
			// and where they ended up.
			if (matchesProductId(inputValue)) {
				// TODO: deal with possible failures by taking the user to /seearch with the query?
				const results = await fetchEndpointData({
					name: 'PostSearchAdvanced',
					body: {
						searchTerm: inputValue,
						// Trust the ranking and treat the top product as the direct hit,
						// instead of requiring a total of exactly one match.
						products: { offset: 0, pageSize: 1 },
					},
				});

				const products = filterTruthy(
					results?.productSearchResponse?.products ?? [],
					'url',
				);

				singleItem = products.length === 1 ? products[0] : undefined;
			} else {
				// These results can still belong to the previous query while the
				// debounced search is loading. Accepted since submitting fast enough
				// to hit a stale single result is rare outside of product IDs.
				const items = [
					...searchResultProducts,
					...searchResultStores,
					...searchResultInspirationPages,
				];

				singleItem = items.length === 1 ? items[0] : undefined;
			}

			// If the result is a single product, go directly to it.
			navigate(
				singleItem?.url
					? { location: singleItem.url }
					: {
							location: 'search',
							query: { query: inputValue },
						},
			);

			blurInput();
		}
	});

	// The SEO folks don't want headings in the header but a heading should be
	// a proper heading for accessibility, so render a p for the initial HTML
	// response and switch to h2 when it's interacted with.
	const groupTitleTag = isOpen ? 'h2' : 'p';
	const ITEM_SPACING_CLASSES = 'mt-6 md:mt-4';
	const resultTermsGroupId = `${baseId}-result-terms`;
	const resultProductsGroupId = `${baseId}-result-products`;
	const resultTermsIdPrefix = `${baseId}-term-item-`;
	const resultProductsIdPrefix = `${baseId}-product-item-`;
	const resultTermsGroupHeading = hasSpellingSuggestions
		? searchResultsSpellingSuggestionText
		: hasResults
			? searchResultsText
			: popularSearchTermsText;

	return (
		<SearchComboboxWithoutContext
			ref={ref}
			className={className}
			dataCy={dataCy}
			isLoading={isLoadingResults && !showSkeleton}
			loadingDelay={500}
			inputLabel={searchFieldLabel}
			inputPlaceholder={searchFieldPlaceholderText}
			submitButtonLabel={t('general_show_all_search_results_button_text')}
			// Cancel button on smaller screens.
			afterSubmit={
				isOpen ? (
					<Button
						variant="text"
						size="sm"
						underline={false}
						onClick={() => {
							closeCombobox();
							focusAfterSearch();
						}}
						className="ml-4 hover:underline headerRow:hidden"
					>
						{searchCancelButtonText}
					</Button>
				) : null
			}
			formClassName="max-headerRow:static"
			controlsContainerClassName="z-searchField max-headerRow:mx-4"
			openClassName={cn(
				'max-headerRow:fixed',
				'max-headerRow:inset-0',
				'max-headerRow:z-99',
				'max-headerRow:overflow-y-scroll',
				'max-headerRow:overscroll-contain',
				'max-headerRow:m-0',
				'max-headerRow:pt-4',
				'max-headerRow:h-[calc(100%+1px)]',
			)}
			resultsContainerClassName={cn(
				'z-searchResults',
				'max-headerRow:inset-0',
				// Change top padding to border to avoid having scrolling content
				// going behind and above the search input.
				'max-headerRow:pt-0',
				'max-headerRow:border-t-[5rem]',
				'max-headerRow:border-t-inverse',
				'max-headerRow:forced-colors:border-t-[Canvas]',
				'max-headerRow:rounded-none',
				'max-headerRow:shadow-none',
			)}
		>
			<div
				className="relative mt-2 p-2 headerRow:mt-4 headerRow:p-4"
				style={
					isMaxMd && keyboardSize
						? // Add 8px for the p-2 that gets overwritten.
							{ paddingBottom: `${keyboardSize + 8}px` }
						: undefined
				}
				data-cy={getTestDataAttrFrom('search-results')}
			>
				{showSkeleton && (
					<LoadingSkeleton className="max-headerRow:mt-4">
						<div className="grid gap-6 md:grid-cols-2 md:gap-4">
							<div className="col-span-1 space-y-6 md:space-y-4">
								<LoadingSkeletonItem height="1.5rem" />
								<LoadingSkeletonItem height="1.5rem" />
							</div>
							<div className="col-span-1 space-y-6 md:space-y-4">
								<LoadingSkeletonItem height="2.5rem" />
								<LoadingSkeletonItem height="2.5rem" />
							</div>
						</div>
					</LoadingSkeleton>
				)}

				{/* Live regions should stay in the DOM and have their content
						changed rather than being added and removed. Be assertive
						about no results since that's important to know. */}
				<Text as="p" aria-live="assertive" aria-atomic="true">
					{showNoResultsText && (
						<>
							{searchNoResultsText}
							<strong className="[quotes:auto] before:content-[open-quote] after:content-[close-quote]">
								{resultQuery}
							</strong>
							…
						</>
					)}
				</Text>
				{showNoResultsText && (
					<Text as="h3" className="mt-12">
						{searchOtherSearchTermRecommendationsText}
					</Text>
				)}

				<div
					{...listboxProps}
					className={cn(
						'headerRow:grid headerRow:grid-cols-2',
						hasResults && '-mt-4',
					)}
				>
					<ComboboxOptionGroup
						titleId={resultTermsGroupId}
						groupTitle={
							<Text
								as={groupTitleTag}
								styleAs="h3"
								className={isGroupTitleHidden ? 'sr-only' : undefined}
							>
								{resultTermsGroupHeading}
							</Text>
						}
					>
						{isLoadingPopularTerms && showPopular && (
							<li role="presentation">
								<LoadingSkeleton>
									{range(termsPageSize).map((i) => (
										<LoadingSkeletonItem
											key={i}
											width={`${50 + (7 % (i + 1)) * 7}px`}
											height="24px"
											className={ITEM_SPACING_CLASSES}
										/>
									))}
								</LoadingSkeleton>
							</li>
						)}
						{showItems &&
							termItems.map((item, i) => (
								<ComboboxOption
									key={item.key}
									id={`${resultTermsIdPrefix}${i + 1}`}
									selectedId={selectedOptionId}
									className={`w-fit pr-1 ${ITEM_SPACING_CLASSES}`}
								>
									<Link
										href={item.url}
										className="group inline-flex items-center align-top font-body text-md"
										tabIndex={-1}
										onClick={item.sendGTMEvent}
										aria-label={
											item.screenReaderPrefix
												? `${item.screenReaderPrefix}: ${item.text} ${item.suffix || ''}`
												: undefined
										}
									>
										{item.icon && <Icon name={item.icon} className="mr-2" />}
										<span className="group-hover:underline">
											<HighlightedText
												text={item.text}
												highlight={hasResults ? resultQuery : undefined}
											/>
											{item.suffix && <> {item.suffix}</>}
										</span>
									</Link>
								</ComboboxOption>
							))}
					</ComboboxOptionGroup>
					<ComboboxOptionGroup
						titleId={resultProductsGroupId}
						groupTitle={
							<Text
								as={groupTitleTag}
								styleAs="h3"
								className={isGroupTitleHidden ? 'sr-only' : undefined}
							>
								{/* No-break space to let the heading occupy its natural
										height since the actual text is always hidden. */}
								<span className="max-headerRow:hidden">&nbsp;</span>
								<span className="sr-only">
									{hasResults ? searchResultProductsText : popularProductsText}
								</span>
							</Text>
						}
					>
						{isLoadingPopularProducts && showPopular && (
							<li role="presentation">
								<LoadingSkeleton>
									{range(productsPageSize).map((i) => (
										<LoadingSkeletonItem
											key={i}
											height="2.5rem"
											className={ITEM_SPACING_CLASSES}
										/>
									))}
								</LoadingSkeleton>
							</li>
						)}
						{showItems &&
							productItems?.map((product, i) => (
								<ComboboxOption
									key={product.id}
									id={`${resultProductsIdPrefix}${product.id}`}
									selectedId={selectedOptionId}
									className={ITEM_SPACING_CLASSES}
								>
									<ProductLine
										highlight={hasResults ? resultQuery : undefined}
										product={product}
										tabIndex={-1}
										onClick={() => {
											sendSelectItemEvent(product, i);
										}}
									/>
								</ComboboxOption>
							))}
					</ComboboxOptionGroup>
				</div>

				{showItems && totalProductCount > productsPageSize && (
					<div className="mt-8 flex justify-center">
						<Button
							href={getSearchPageUrl(search, searchQuery)}
							className="max-sm:w-full sm:min-w-80"
						>
							{t('general_show_all_search_results_button_text')}{' '}
							<span className="ml-1 font-normal">({totalProductCount})</span>
						</Button>
					</div>
				)}
			</div>
		</SearchComboboxWithoutContext>
	);
}
PageHeaderSearchCombobox.displayName = 'PageHeaderSearchCombobox';

/** Search field with results dropdown. */
export default function PageHeaderSearch({
	id,
	onClose,
	ref,
	...innerProps
}: Props) {
	return (
		<SearchComboboxContextProvider id={id} onClose={onClose}>
			<PageHeaderSearchCombobox ref={ref} {...innerProps} />
		</SearchComboboxContextProvider>
	);
}
PageHeaderSearch.displayName = 'PageHeaderSearch';
